import { Button, Kind } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { alert } from 'actions/flash'
import {
  RevenueCenter,
  RevenueCenterConfigRow as ConfigRow,
  useUpsertRevenueCenterConfigMutation,
} from 'api/revenueCenters'
import { buildSyncRowLabels } from 'components/buildSyncRowLabels'
import Section from 'components/Section'
import SyncRow from 'components/SyncRow'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import RevenueCenterPicker from './RevenueCenterPicker'

const t = buildTranslate('cms.settings.revenue_centers')

type Props = {
  revenueCenters: RevenueCenter[]
  defaultConfig: ConfigRow | undefined
  onSyncNow: () => void
  providerName: string
  syncedAt?: string | null
  isSyncing?: boolean
}

const DefaultTab = ({
  revenueCenters,
  defaultConfig,
  onSyncNow,
  providerName,
  syncedAt,
  isSyncing = false,
}: Props) => {
  const dispatch = useDispatch()
  const [isEditing, setIsEditing] = useState(false)
  const [draftName, setDraftName] = useState<string | undefined>(undefined)
  const [upsert, { isLoading: isSaving }] =
    useUpsertRevenueCenterConfigMutation()

  const syncLabels = buildSyncRowLabels(providerName, syncedAt)

  const currentName = defaultConfig?.revenue_center_name
  // A name can be ACTIVE at some locations and ARCHIVED at others; treat it as
  // archived only when no ACTIVE catalog entry exists, so the warning banner
  // doesn't hinge on catalog response order.
  const matchingCenters = currentName
    ? revenueCenters.filter(rc => rc.name === currentName)
    : []
  const isArchived =
    matchingCenters.length > 0 &&
    !matchingCenters.some(rc => rc.state === 'ACTIVE')

  const startEditing = () => {
    setDraftName(currentName)
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setDraftName(undefined)
    setIsEditing(false)
  }

  const onSave = async () => {
    if (!draftName) return
    if (draftName === currentName) {
      cancelEditing()
      return
    }
    try {
      await upsert({ revenue_center_name: draftName }).unwrap()
      dispatch(alert({ key: 'success', message: t('save_success') }))
      setIsEditing(false)
      setDraftName(undefined)
    } catch {
      dispatch(alert({ key: 'danger', message: t('save_error') }))
    }
  }

  return (
    <>
      <SyncRow
        onSyncNow={onSyncNow}
        syncedAt={syncedAt}
        isSyncing={isSyncing}
        labels={syncLabels}
      />
      <Section className="p-l">
        <div className="d-flex justify-content-between align-items-start mb-xs">
          <Text.Header4 className="m-0">
            {t('default_tab.section_title')}
          </Text.Header4>
          {!isEditing && (
            <Button kind={Kind.SECONDARY} onClick={startEditing}>
              {t('row.edit')}
            </Button>
          )}
        </div>
        <Text.BodyText4 color="grey70" tag="p" className="mb-m">
          {t('default_tab.helper')}
        </Text.BodyText4>

        <div id="default-rc-field-label" className="mb-xxs">
          <Text.SmallCaps3 color="grey70" tag="div">
            {t('default_tab.field_label')}
          </Text.SmallCaps3>
        </div>

        {isEditing ? (
          <div className="mb-m">
            <RevenueCenterPicker
              revenueCenters={revenueCenters}
              value={draftName}
              onChange={setDraftName}
              ariaLabelledBy="default-rc-field-label"
            />
            <div className="mt-s d-flex">
              <Button
                kind={Kind.PRIMARY}
                onClick={onSave}
                disabled={!draftName}
                isLoading={isSaving}
                className="mr-s"
              >
                {t('row.save')}
              </Button>
              <Button
                kind={Kind.MINIMAL}
                onClick={cancelEditing}
                disabled={isSaving}
              >
                {t('row.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="mb-m" data-testid="default-rc-display">
            {currentName ? (
              <Text.BodyText3 bold color="grey90" tag="div">
                {currentName}
              </Text.BodyText3>
            ) : (
              <Text.BodyText4 color="grey50">
                {t('default_tab.empty')}
              </Text.BodyText4>
            )}
          </div>
        )}

        {!isEditing && currentName && (
          <Text.BodyText5 color="grey70" tag="p" className="mb-m">
            {t('default_tab.footnote', { provider: providerName })}
          </Text.BodyText5>
        )}

        <Notification kind="info" size="full">
          <Text.BodyText4 color="grey70" className="p-xs">
            {t('default_tab.scope')}
          </Text.BodyText4>
        </Notification>

        {isArchived && currentName && (
          <div className="mt-m" data-testid="archived-banner" role="alert">
            <Notification kind="negative" size="full">
              <div className="p-xs">
                <Text.BodyText3 bold color="grey90" tag="div" className="mb-xs">
                  {t('archived_banner.title', {
                    name: currentName,
                    provider: providerName,
                  })}
                </Text.BodyText3>
                <Text.BodyText4 color="grey70" tag="p" className="mb-s">
                  {t('archived_banner.body', {
                    name: currentName,
                    provider: providerName,
                  })}
                </Text.BodyText4>
                <Button kind={Kind.PRIMARY} onClick={startEditing}>
                  {t('archived_banner.cta')}
                </Button>
              </div>
            </Notification>
          </div>
        )}
      </Section>
    </>
  )
}

export default DefaultTab
